<script setup lang="ts">
/**
 * Aura AGM — [[변수]] 자리. 기존 인코퍼레이션 칩 스타일과 동일:
 *   빈칸=크림칩, 채움=파란칩, 아이콘 원(✨ lookup / 📅 date / 📍 address).
 *   text/person/entity/address = contenteditable + 아이콘, select/yesno = 별도 위젯.
 */
import { computed, inject, nextTick, onMounted, onBeforeUnmount, ref, watch } from 'vue';
import { useAgmFill, DOC_SCOPE } from './useCddFill';
import { useSessionStore } from '@/shared/stores/sessionStore';
import { formatDateOnly } from '@/shared/composables/dateUtils';

const props = defineProps<{
  fieldKey: string;
  label?: string;
  options?: Array<{ value: string; title: string }>;
  compact?: boolean;
}>();

const fill = useAgmFill();
const scope = inject(DOC_SCOPE, '');
const fullKey = computed(() => scope + props.fieldKey);
const kind = computed(() => fill.typeOf(fullKey.value));
const sessionStore = useSessionStore();

const value = computed(() => fill.getValue(fullKey.value));
const active = computed(() => fill.activeKey.value === fullKey.value);
const label = computed(() => props.label || props.fieldKey);

const isDate = computed(() => kind.value === 'date');
const isAddress = computed(() => kind.value === 'address');
const isLookup = computed(() => kind.value === 'person' || kind.value === 'entity');
const isSelect = computed(() => kind.value === 'select');
const isYesNo = computed(() => kind.value === 'yesno');

const _dfFormat = computed(() => (sessionStore.user as any)?.user?.dateFormat || 'YYYY-MM-DD');
function toDateDisplay(v: string): string {
  if (!v) return '';
  // 패널이 provideAgmFill({ formatDate }) 로 표기를 지정하면 그걸 우선(예: Singapura "30 June 2026").
  if (fill.formatDate) return fill.formatDate(v) || v;
  // ⚠ 세션 dateFormat 은 'dd/MM/yyyy' 처럼 **소문자 토큰**이 기본이다. 여기서 직접
  //   replace(/YYYY|MM|DD/) 로 치환하면 dd/yyyy 가 남아 'dd/12/yyyy' 로 보이고
  //   (사용자 눈엔 날짜가 안 들어간 것처럼 보인다) → 공용 formatDateOnly 로 통일.
  return formatDateOnly(v, _dfFormat.value) || v;
}
const displayText = computed(() => (isDate.value ? toDateDisplay(value.value) : value.value));

/* ── contenteditable 동기화 ── */
const ed = ref<HTMLElement | null>(null);
let focused = false;
function syncEd() {
  if (!ed.value || focused) return;
  const shown = displayText.value || '';
  if (ed.value.innerText !== shown) ed.value.innerText = shown;
}
watch(displayText, () => nextTick(syncEd));
onMounted(() => { fill.registerField(fullKey.value); nextTick(syncEd); });
onBeforeUnmount(() => fill.unregisterField(fullKey.value));
watch(fullKey, (n, o) => { fill.unregisterField(o); fill.registerField(n); nextTick(syncEd); });

function onInput() {
  if (!ed.value) return;
  if (isDate.value) return; // 날짜는 피커로만
  fill.setValue(fullKey.value, ed.value.innerText.replace(/\n+/g, ' ').trim());
}
function onFocus() { focused = true; fill.markActive(fullKey.value); }
function onBlur() {
  focused = false;
  // 날짜: 글자를 모두 지우면 값도 비운다 (재입력은 피커로만)
  if (isDate.value && ed.value && !ed.value.innerText.trim()) fill.setValue(fullKey.value, '');
  onInput();
  nextTick(syncEd);
}
function onTextClick() { fill.markActive(fullKey.value); }

/* ── date picker ── */
const dateMenu = ref(false);
function onPickDate(d: unknown) {
  const dt = d instanceof Date ? d : new Date(String(d));
  if (!Number.isNaN(dt.getTime())) {
    const iso = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
    fill.setValue(fullKey.value, iso);
  }
  dateMenu.value = false;
}
function onDateIconClick() { fill.markActive(fullKey.value); dateMenu.value = true; }
function clearDate() {
  fill.setValue(fullKey.value, '');
  if (ed.value) ed.value.innerText = '';
  dateMenu.value = false;
}
function onAddressClick() { fill.requestAddress(fullKey.value); }
function onLookupClick() { fill.activate(fullKey.value); }

/* ── select / yesno ── */
function onSelect(v: string) { fill.setValue(fullKey.value, v); }
const ynNormalized = computed(() => {
  const v = String(value.value || '').trim().toLowerCase();
  return v === 'yes' || v === 'true' || v === '1' ? 'yes' : v === 'no' || v === 'false' || v === '0' ? 'no' : '';
});
function setYesNo(v: 'yes' | 'no') {
  fill.setValue(fullKey.value, ynNormalized.value === v ? '' : v);
}
</script>

<template>
  <!-- Yes / No -->
  <span v-if="isYesNo" class="lv-field lv-yn" :class="{ empty: !ynNormalized, filled: !!ynNormalized, active }" :title="label">
    <span class="lv-yn-opt" :class="{ on: ynNormalized === 'yes' }" @click.stop="setYesNo('yes')"><span class="box">{{ ynNormalized === 'yes' ? '✓' : '' }}</span> Yes</span>
    <span class="lv-yn-opt" :class="{ on: ynNormalized === 'no' }" @click.stop="setYesNo('no')"><span class="box">{{ ynNormalized === 'no' ? '✓' : '' }}</span> No</span>
  </span>

  <!-- Select -->
  <span v-else-if="isSelect" class="lv-field" :class="{ empty: !value, filled: !!value, active }" :title="label">
    <select class="lv-select" :value="value" @change="onSelect(($event.target as HTMLSelectElement).value)">
      <option value="">{{ label }}</option>
      <option v-for="o in (options || [])" :key="o.value" :value="o.value">{{ o.title }}</option>
    </select>
  </span>

  <!-- text / person / entity / address (contenteditable + icons) -->
  <span v-else class="lv-field" :class="{ empty: !value, filled: !!value, active, compact: props.compact }" :title="label">
    <span
      ref="ed"
      class="lv-text"
      contenteditable="true"
      :data-ph="props.compact ? '' : label"
      spellcheck="false"
      @input="onInput"
      @focus="onFocus"
      @blur="onBlur"
      @click.stop="onTextClick"
      @keydown.enter.prevent="ed?.blur()"
    />
    <button v-if="isDate" type="button" class="lv-chat lv-date" title="Pick date" @mousedown.prevent @click="onDateIconClick">
      <v-icon size="12">mdi-calendar-month</v-icon>
    </button>
    <button v-if="isDate && value" type="button" class="lv-chat lv-clear" title="Clear date" @mousedown.prevent @click.stop="clearDate">
      <v-icon size="12">mdi-close</v-icon>
    </button>
    <button v-if="isAddress" type="button" class="lv-chat lv-addr" title="Enter address via chat" @mousedown.prevent @click="onAddressClick">
      <v-icon size="12">mdi-map-marker-outline</v-icon>
    </button>
    <button v-if="isLookup" type="button" class="lv-chat lv-ai" title="Look up Contact via assistant" @mousedown.prevent @click="onLookupClick">
      <v-icon size="12">mdi-creation</v-icon>
    </button>
    <v-menu v-if="isDate" v-model="dateMenu" :close-on-content-click="false" location="bottom">
      <template #activator="{ props: menuProps }"><span class="lv-date-anchor" v-bind="menuProps" /></template>
      <v-date-picker hide-header @update:model-value="onPickDate" />
    </v-menu>
  </span>
</template>

<style scoped>
.lv-field {
  position: relative;
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  cursor: text;
  border-radius: 3px;
  padding: 0 3px;
  white-space: pre-line;
  vertical-align: baseline;
  max-width: 100%;
}
.lv-field.empty { background: #fff6d6; box-shadow: inset 0 -2px 0 #f2c94c; }
.lv-field.empty .lv-text:empty::before { content: attr(data-ph); color: #9a7b1a; font-size: 0.92em; font-style: italic; }
.lv-field.filled { background: #eaf3ff; box-shadow: inset 0 -2px 0 #1b84ff; }
.lv-field.filled .lv-text { color: #10305c; font-weight: 600; }
.lv-field.active { outline: 2px solid #1b84ff; }
.lv-field.compact { padding: 0 1px; }
.lv-text { outline: none; min-width: 46px; overflow-wrap: break-word; word-break: break-word; }

.lv-chat {
  display: inline-flex; align-items: center; justify-content: center;
  width: 18px; height: 18px; border-radius: 50%; border: none; cursor: pointer;
  flex: 0 0 auto; padding: 0;
}
.lv-chat.lv-addr { background: #fdeefe; color: #a23bb0; }
.lv-chat.lv-date { background: #eafaf1; color: #22a06b; }
.lv-chat.lv-clear { background: #f1f3f5; color: #6b7280; }
.lv-chat.lv-ai { background: #eaf3ff; color: #1b84ff; }
.lv-chat:hover { filter: brightness(0.94); }
.lv-date-anchor { position: absolute; left: 0; bottom: 0; width: 1px; height: 1px; }

.lv-yn { gap: 8px; }
.lv-yn-opt { display: inline-flex; align-items: center; gap: 3px; padding: 0 4px; color: #42506a; cursor: pointer; }
.lv-yn-opt.on { color: #10305c; font-weight: 700; }
.lv-yn-opt .box {
  display: inline-flex; align-items: center; justify-content: center;
  width: 14px; height: 14px; border: 1.5px solid #8b95a1; border-radius: 2px; background: #fff; font-size: 10px; color: #1b84ff;
}
.lv-yn-opt.on .box { border-color: #1b84ff; background: #eaf3ff; }
.lv-select { border: none; background: transparent; font: inherit; color: #10305c; max-width: 100%; cursor: pointer; }
</style>
